import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute} from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { LinearGradient } from 'expo-linear-gradient';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  View,
  Text,
  ScrollView,
  Alert,
  ListRenderItem,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { useMultiplayerStore as multiplayerStoreInstance } from "../../../features/quiz-core/multiplayerStore";
import { useMultiplayerStore } from "../../../features/quiz-core/multiplayerStore";
import { useUserProfile } from "../../../features/quiz-core/useStore";
import { t } from '../../../i18n/translations';
import { RootStackParamList } from "../../../navigation/types";
import { supabase } from "../../../shared/services/supabaseService";
import { iqesColors, iqesGradients } from "../../../shared/ui/iqesDesignSystem";
import { useFamilyData } from '../../groups/useFamilyData';
import { entitlementService } from '../../monetization/services/entitlementService';
import { useLanguageStore } from '../../quiz-core/languageStore';
import ActiveRoomsList from "../components/ActiveRoomsList";
import CenteredMessage from "../components/CenteredMessage";
import LobbyItem from "../components/LobbyItem";
import PlayerPreviewModal from "../components/PlayerPreviewModal";
import { useSessionKeepalive } from '../hooks/useSessionKeepalive';
import type { GamePlayer, GameRoom } from "../types/game.types";
import { resolveGroupContextMeta } from "../utils/groupMeta";
import { uiText } from '../../../shared/utils/uiText';

// }

type HostTier = 'premium' | 'free' | 'none';
type LobbyRouteParams = {
  familyId?: string;
  familyName?: string;
  friendsGroupId?: string;
  friendsGroupName?: string;
  groupId?: string;
  groupName?: string;
  groupType?: 'family' | 'friends';
};
type UserFamilySummary = {
  id: string;
};
type RoomsChannelCleanup = (() => void) | { unsubscribe?: () => void } | null;

export default function MultiplayerLobbyScreen() {
  /* ----------------------------- LOCAL STATE ----------------------------- */
  // Keep session alive during multiplayer lobby
  useSessionKeepalive(true);
  const [previewRoomName, setPreviewRoomName] = useState("");
  const [previewRoomId, setPreviewRoomId] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [hostTier, setHostTier] = useState<HostTier>('none');
  const [sponsorAccessLoading, setSponsorAccessLoading] = useState(true);
  const canCreateRoom = hostTier !== 'none';
  const isPremiumHost = hostTier === 'premium';
  const publicOnly = true;

  /* ------------------------------- STORES -------------------------------- */
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

  const route = useRoute();

  // Seed stress test data in development builds (run only once on mount)
  // Clear any existing dummy data from the store on mount
  useEffect(() => {
    if (__DEV__) {
      try {
        // Clear dummy data by resetting chat messages and players to empty arrays
        multiplayerStoreInstance.setState({
          chatMessages: [],
          players: [],
          currentRoom: null
        });
        console.log('[MultiplayerLobby] Cleared dummy data from store');
      } catch (e) {
        console.warn('[MultiplayerLobby] Failed to clear dummy data:', e);
      }
    }
    // empty dependency array ⇒ run once
  }, []);

  // DISABLED: Uncomment the lines below to enable stress testing with dummy data
  // useEffect(() => {
  //   } catch (e) {
  //     }
  //   }
  //   // empty dependency array ⇒ run once
  // }, []);

  // Accept group parameters from navigation - supports all group types
  const {
    familyId,
    familyName,
    friendsGroupId,
    friendsGroupName,
    groupId: directGroupId,
    groupName: directGroupName,
    groupType,
  }: LobbyRouteParams = (route.params || {}) as LobbyRouteParams;

  // Support all group contexts with priority: direct params > legacy params
  const groupId = directGroupId || familyId || friendsGroupId;
  const groupName = directGroupName || familyName || friendsGroupName;
  const resolvedGroupType = groupType || (familyId ? 'family' : friendsGroupId ? 'friends' : 'family');

  const {
    rooms,
    loading: roomsLoading,
    fetchRooms,
    joinRoom,
    fetchPlayers,
    players,
    listenPlayers,
    fetchRoomById,
    currentRoom,
  } = useMultiplayerStore();

  const profile = useUserProfile();
  const { language } = useLanguageStore();

  // --- Group membership for filtering rooms ---
  const { families: userFamilies, loading: familiesLoading } = useFamilyData(profile.id);
  const userFamilyIds = useMemo(() => (userFamilies || []).map((fam: UserFamilySummary) => fam.id), [userFamilies]);

  // Determine active group IDs based on context
  const activeGroupIds = groupId ? [groupId] : userFamilyIds;

  // Diagnostic logging (DEV only)
  useEffect(() => {
    if (!__DEV__) return;
    console.log('[MultiplayerLobbyScreen] Profile:', profile, 'groupId:', groupId, 'groupType:', resolvedGroupType);
    if (profile?.error) {
      console.error('[MultiplayerLobbyScreen] Profile error:', profile.error, profile);
    }
  }, [profile, groupId, resolvedGroupType]);

  // --- Filter rooms based on group type and membership ---
  const roomMatchesActiveGroup = useCallback((room: GameRoom) => {
    const roomType = room.room_type ?? 'family';
    if (roomType !== resolvedGroupType) {
      return false;
    }

    const roomGroupId =
      resolvedGroupType === 'friends' ? room.friends_group_id : room.family_id;

    if (groupId) {
      return roomGroupId === groupId;
    }

    if (resolvedGroupType === 'family') {
      return !!room.family_id && userFamilyIds.includes(room.family_id);
    }

    return !!room.friends_group_id;
  }, [groupId, resolvedGroupType, userFamilyIds]);

  const filteredRooms = useMemo(() => {
    if (!activeGroupIds || activeGroupIds.length === 0) return [];
    return rooms.filter(roomMatchesActiveGroup);
  }, [rooms, activeGroupIds, roomMatchesActiveGroup]);

  const groupMeta = useMemo(
    () =>
      resolveGroupContextMeta(
        {
          familyId,
          familyName,
          friendsGroupId,
          friendsGroupName,
          groupId,
          groupName,
          groupType: resolvedGroupType,
        },
        language,
      ),
    [
      familyId,
      familyName,
      friendsGroupId,
      friendsGroupName,
      groupId,
      groupName,
      language,
      resolvedGroupType,
    ],
  );
  const normalizedFamilyId = resolvedGroupType === 'family' ? (familyId || groupId) : undefined;
  const normalizedFamilyName = resolvedGroupType === 'family' ? (familyName || groupName) : undefined;
  const normalizedFriendsGroupId =
    resolvedGroupType === 'friends' ? (friendsGroupId || groupId) : undefined;
  const normalizedFriendsGroupName =
    resolvedGroupType === 'friends' ? (friendsGroupName || groupName) : undefined;
  const groupLabel = groupMeta.groupLabel;
  const groupLabelLower = groupMeta.groupLabelLower;
  const normalizedGroupName = (() => {
    const candidate = groupName?.trim() || '';
    const normalized = candidate.toLowerCase();
    if (!candidate) return '';
    if (normalized === 'new game room' || normalized === 'new lobby') return '';
    return candidate;
  })();
  const heroTitle = normalizedGroupName
    ? t('multiplayer_lobby.groupGameRoom', language).replace('{{groupName}}', normalizedGroupName)
    : t('multiplayer_lobby.groupLobbyTitle', language).replace('{{groupType}}', groupLabel);
  const hasActiveRooms = filteredRooms.length > 0;
  const gameRoomParams = useMemo(
    () => ({
      familyId: normalizedFamilyId,
      familyName: normalizedFamilyName,
      friendsGroupId: normalizedFriendsGroupId,
      friendsGroupName: normalizedFriendsGroupName,
    }),
    [
      normalizedFamilyId,
      normalizedFamilyName,
      normalizedFriendsGroupId,
      normalizedFriendsGroupName,
    ],
  );

  useEffect(() => {
    let active = true;

    const checkSponsorAccess = async () => {
      if (!profile.id) {
        if (active) {
          setHostTier('none');
          setSponsorAccessLoading(false);
        }
        return;
      }

      setSponsorAccessLoading(true);

      try {
        const hasDirectPremium = await entitlementService.hasPremiumHostAccess(profile.id);
        if (active) {
          setHostTier(hasDirectPremium ? 'premium' : 'free');
        }
      } catch {
        if (active) {
          setHostTier('free'); // Default to free (can still create capped rooms)
        }
      } finally {
        if (active) {
          setSponsorAccessLoading(false);
        }
      }
    };

    checkSponsorAccess();

    return () => {
      active = false;
    };
  }, [profile.id]);

  const supportDescription = useMemo(() => {
    if (hasActiveRooms) {
      if (isPremiumHost) return t('multiplayer_lobby.hostActiveDescription', language).replace('{{groupType}}', groupLabelLower);
      if (canCreateRoom) return t('multiplayer_lobby.freeHostActiveDescription', language).replace('{{groupType}}', groupLabelLower);
      return t('multiplayer_lobby.memberActiveDescription', language).replace('{{groupType}}', groupLabelLower);
    }
    if (isPremiumHost) return t('multiplayer_lobby.hostEmptyDescription', language).replace('{{groupType}}', groupLabelLower);
    if (canCreateRoom) return t('multiplayer_lobby.freeHostEmptyDescription', language).replace('{{groupType}}', groupLabelLower);
    return t('multiplayer_lobby.memberRoomDescription', language).replace('{{groupType}}', groupLabelLower);
  }, [isPremiumHost, canCreateRoom, groupLabelLower, hasActiveRooms, language]);

  const sectionEyebrow = hasActiveRooms
    ? (isPremiumHost ? t('multiplayer_lobby.hostActiveEyebrow', language)
       : canCreateRoom ? t('multiplayer_lobby.freeHostActiveEyebrow', language)
       : t('multiplayer_lobby.memberActiveEyebrow', language))
    : (isPremiumHost ? t('multiplayer_lobby.hostEmptyEyebrow', language)
       : canCreateRoom ? t('multiplayer_lobby.freeHostEmptyEyebrow', language)
       : t('multiplayer_lobby.memberBadge', language));
  const sectionTitle = hasActiveRooms
    ? (isPremiumHost ? t('multiplayer_lobby.hostActiveTitle', language).replace('{{groupType}}', groupLabelLower)
       : canCreateRoom ? t('multiplayer_lobby.freeHostActiveTitle', language).replace('{{groupType}}', groupLabelLower)
       : t('multiplayer_lobby.memberActiveTitle', language))
    : (isPremiumHost ? t('multiplayer_lobby.hostEmptyTitle', language).replace('{{groupType}}', groupLabelLower)
       : canCreateRoom ? t('multiplayer_lobby.freeHostEmptyTitle', language).replace('{{groupType}}', groupLabelLower)
       : t('multiplayer_lobby.memberRoomTitle', language));
  const createButtonLabel = t('multiplayer_lobby.startGroupQuiz', language).replace('{{groupType}}', groupLabelLower);
  const emptyRoomsTitle = isPremiumHost
    ? t('multiplayerLobby.noActiveRooms')
    : canCreateRoom ? t('multiplayer_lobby.freeHostEmptyTitle', language).replace('{{groupType}}', groupLabelLower)
    : t('multiplayer_lobby.memberRoomEmptyTitle', language);
  const emptyRoomsSubtitle = isPremiumHost
    ? t('multiplayer_lobby.hostEmptyDescription', language).replace('{{groupType}}', groupLabelLower)
    : canCreateRoom ? t('multiplayer_lobby.freeHostEmptyDescription', language).replace('{{groupType}}', groupLabelLower)
    : t('multiplayer_lobby.memberRoomEmptyDescription', language).replace('{{groupType}}', groupLabelLower);

  // --- Removed Chat Integration for MVP ---
  // Chat functionality removed to improve UX and focus on core gameplay
  // This reclaims ~50% of screen space on mobile devices

  // --- Rematch Listener: Auto-navigate if rematch_room_id is set for currentRoom ---
  useEffect(() => {
    if (!currentRoom?.id) return;
    let interval: NodeJS.Timeout | null = null;
    let navigating = false;
    interval = setInterval(async () => {
      if (navigating) return;
      const { data } = await supabase
        .from("multiplayer_game_rooms")
        .select("rematch_room_id")
        .eq("id", currentRoom.id)
        .single();
      if (data?.rematch_room_id) {
        navigating = true;
        console.log(
          `[Rematch] (Lobby) Player (user_id: ${profile.id}) detected rematch_room_id: ${data.rematch_room_id} and is joining rematch Game Room.`,
        );
        setTimeout(() => {
          navigation.reset({
            index: 0,
            routes: [
              { name: "GameRoom", params: { roomId: data.rematch_room_id, ...gameRoomParams } },
            ],
          });
        }, 800);
      }
    }, 2500);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentRoom?.id, gameRoomParams, navigation, profile.id]);

  // --- Removed Presence Integration for MVP ---
  // Presence/chat functionality removed to focus on core game experience

  // Refresh player data when profile changes
  useEffect(() => {
    if (rooms.length > 0 && profile.name) {
      // Refresh player data for all rooms to ensure latest profile info is displayed
      rooms.forEach((room) => {
        fetchPlayers(room.id);
      });
    }
  }, [profile.name, rooms, fetchPlayers]);

  // Effect to refresh player data when preview modal is opened and set up real-time updates
  useEffect(() => {
    if (showPreviewModal && previewRoomId) {
      setPreviewLoading(true);

      // Fetch the room details first
      fetchRoomById(previewRoomId)
        .then(() => {
          // Then fetch the players
          return fetchPlayers(previewRoomId);
        })
        .finally(() => {
          setPreviewLoading(false);
        });

      // Set up real-time listener for player changes
      const playerChannel = listenPlayers(previewRoomId, () => {
        console.log("[PreviewModal] Player changes detected, refreshing data");
        fetchPlayers(previewRoomId);
      });

      // Clean up listener when modal closes
      return () => {
        if (playerChannel) {
          playerChannel.unsubscribe();
        }
      };
    }
  }, [
    showPreviewModal,
    previewRoomId,
    fetchPlayers,
    listenPlayers,
    fetchRoomById,
  ]);

  // --- Removed Typing Indicators for MVP ---
  // Chat-related state removed to simplify component

  /* ----------------------------- REMOTE FETCH ---------------------------- */
  // Use a ref to track if the component is mounted
  const isMounted = useRef(true);

  // Safe fetch function that checks if component is still mounted
  const safeFetchRooms = useCallback(async () => {
    if (isMounted.current) {
      try {
        await fetchRooms();
        console.log("[MultiplayerLobby] Rooms fetched successfully");
      } catch (error) {
        console.error("[MultiplayerLobby] Error fetching rooms:", error);
      }
    }
  }, [fetchRooms]);

  useEffect(() => {
    // Set mounted flag
    isMounted.current = true;

    // Initial fetch of rooms
    safeFetchRooms();

    let roomsChannel: RoomsChannelCleanup = null; // Can be cleanup function or channel-like object
    let pollInterval: NodeJS.Timeout | null = null;

    // Set up the real-time listener with proper error handling
    const setupRealtime = () => {
      try {
        const { listenActiveRooms } = useMultiplayerStore.getState();

        console.log(
          "[MultiplayerLobby] Setting up real-time listener for active rooms",
        );
        roomsChannel = listenActiveRooms(() => {
          if (!isMounted.current) return;

          console.log(
            "[MultiplayerLobby] Room changes detected, refreshing data",
          );
          // Use a small delay to ensure the database has completed its operation
          setTimeout(() => {
            if (isMounted.current) {
              safeFetchRooms();
            }
          }, 300);
        });

        // Set up a polling interval as a fallback to ensure data stays fresh
        // Using MMKV or AsyncStorage to track last refresh time would be better for production
        pollInterval = setInterval(() => {
          if (!isMounted.current) return;

          console.log("[MultiplayerLobby] Polling for room updates");
          safeFetchRooms();
        }, 15000); // Poll every 15 seconds as a fallback
      } catch (error) {
        console.error("[MultiplayerLobby] Error setting up realtime:", error);
        // If realtime setup fails, still set up polling as a fallback
        if (!pollInterval && isMounted.current) {
          pollInterval = setInterval(() => {
            if (!isMounted.current) return;

            console.log("[MultiplayerLobby] Fallback polling for room updates");
            safeFetchRooms();
          }, 5000); // More frequent polling if realtime failed
        }
      }
    };

    // Set up the realtime listener
    setupRealtime();

    // Clean up listeners and intervals when component unmounts
    return () => {
      // Set mounted flag to false to prevent state updates after unmount
      isMounted.current = false;

      if (roomsChannel) {
        console.log("[MultiplayerLobby] Unsubscribing from room changes");
        try {
          // roomsChannel is a cleanup function, not a channel object
          if (typeof roomsChannel === 'function') {
            roomsChannel(); // Call the cleanup function
          } else {
            // Fallback: if it's actually a channel object
            supabase.removeChannel(roomsChannel);
          }
        } catch (e) {
          console.error(
            "[MultiplayerLobby] Error unsubscribing from channel:",
            e,
          );
        }
      }

      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [publicOnly, safeFetchRooms]);

  /* --------------------------- CALLBACK HELPERS -------------------------- */
  const handleCreateRoom = useCallback(() => {
    if (!canCreateRoom) {
      Alert.alert(
        t('multiplayer_lobby.memberRoomTitle', language),
        t('multiplayer_lobby.memberRoomDescription', language).replace('{{groupType}}', groupLabelLower),
      );
      return;
    }

    navigation.navigate("MultiplayerCategory", {
      familyId: normalizedFamilyId,
      familyName: normalizedFamilyName,
      friendsGroupId: normalizedFriendsGroupId,
      friendsGroupName: normalizedFriendsGroupName,
    });
  }, [
    canCreateRoom,
    groupLabelLower,
    language,
    navigation,
    normalizedFamilyId,
    normalizedFamilyName,
    normalizedFriendsGroupId,
    normalizedFriendsGroupName,
  ]);

  const handlePreviewPlayers = useCallback(
    (roomId: string, roomTitle: string) => {
      setPreviewRoomId(roomId);
      setPreviewRoomName(roomTitle);
      setPreviewLoading(true);
      setShowPreviewModal(true);
      // Fetch players, etc.
    },
    [setPreviewRoomId, setPreviewRoomName, setShowPreviewModal],
  );

  /* --------------------------- RENDER FUNCTIONS -------------------------- */
  const renderLobby: ListRenderItem<GameRoom> = useCallback(
    ({ item }) => {
      const isHost = item.host_id === profile.id;
      return (
        <LobbyItem
          item={item}
          isHost={isHost}
          groupTypeOverride={resolvedGroupType}
          groupNameOverride={groupMeta.groupName}
          onPreview={() => handlePreviewPlayers(item.id, `Room ${item.code}`)}
          onJoin={async () => {
            console.log(isHost ? "Entering room:" : "Joining room:", item.code);
            if (isHost) {
              // Host already has a player record in this room, just navigate
              navigation.navigate("GameRoom", { roomId: item.id, ...gameRoomParams });
            } else {
              // Guest needs to join the room first
              try {
                console.log("Attempting to join room with code:", item.code);
                console.log("Profile data:", {
                  id: profile.id,
                  name: profile.name,
                  avatar_url: profile.avatar_url,
                });

                const result = await joinRoom({
                  code: item.code,
                  user_id: profile.id,
                  name: profile.name || "Guest",
                  avatar_url: profile.avatar_url,
                });

                console.log("Join room result:", result);

                if (result.success) {
                  console.log(
                    "Successfully joined room, navigating to GameRoom with ID:",
                    result.room.id,
                  );
                  navigation.navigate("GameRoom", { roomId: result.room.id, ...gameRoomParams });
                } else if (result.reason === 'room_full') {
                  const roomMaxPlayers = item?.max_players ?? 0;
                  if (roomMaxPlayers <= 3) {
                    Alert.alert(
                      t('multiplayer_lobby.freeRoomFullTitle', language),
                      t('multiplayer_lobby.freeRoomFullMessage', language),
                    );
                  } else {
                    Alert.alert(uiText('errorTitle'), t('multiplayer_lobby.roomFullGeneric', language));
                  }
                } else {
                  Alert.alert(
                    uiText('errorTitle'),
                    result.message || uiText('joinRoomFailed'),
                  );
                }
              } catch (error: unknown) {
                console.error("Error joining room:", error);
                const message = error instanceof Error ? error.message : uiText('unknownError');
                Alert.alert(
                  uiText('errorTitle'),
                  uiText('joinRoomFailedWithReason', { message }),
                );
              }
            }
          }}
          data-oid="foa.3qp"
        />
      );
    },
    [gameRoomParams, handlePreviewPlayers, joinRoom, navigation, profile],
  );

  /* ----------------------------- RENDER FLOW ----------------------------- */
  if (profile.loading)
    return (
      <LinearGradient colors={iqesGradients.appBackground} style={styles.centeredShell}>
        <StatusBar barStyle="light-content" backgroundColor={iqesColors.canvas} />
        <ActivityIndicator size="large" color={iqesColors.goldBright} />
        <Text style={styles.centeredShellText}>{t('multiplayer_lobby.loadingProfile', language)}</Text>
      </LinearGradient>
    );

  if (profile.error)
    return (
      <LinearGradient colors={iqesGradients.appBackground} style={styles.centeredShell}>
        <StatusBar barStyle="light-content" backgroundColor={iqesColors.canvas} />
        <CenteredMessage
          title={t('multiplayer_lobby.profileLoadFailed', language)}
          subtitle={profile.error}
          error
          data-oid="vn40rcu"
        />
      </LinearGradient>
    );

return (
  <LinearGradient colors={iqesGradients.appBackground} style={styles.screen}>
    <StatusBar barStyle="light-content" backgroundColor={iqesColors.canvas} />
    <View style={styles.orbTop} pointerEvents="none" />
    <View style={styles.orbBottom} pointerEvents="none" />
    <SafeAreaView style={{ flex: 1 }} edges={["top", "left", "right"]}>
      <View style={{ flex: 1 }}>
        {/* Main Content - Scrollable */}
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ flexGrow: 1, paddingBottom: 136 }}
          style={{ flex: 1 }}
          data-oid="ym3cyov"
        >

        {/* Premium Header */}
        <View style={styles.headerContainer}>
          {/* Back Button */}
          <View style={{
            position: 'absolute',
            left: 24,
            top: 8,
            zIndex: 10,
          }}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                backgroundColor: 'rgba(8, 10, 24, 0.92)',
                alignItems: 'center',
                justifyContent: 'center',
                borderWidth: 1,
                borderColor: 'rgba(246, 190, 58, 0.38)',
              }}
              accessibilityLabel={t('multiplayer_lobby.goBackAccessibility', language)}
              accessibilityRole="button"
            >
              <Ionicons name="arrow-back" size={20} color={iqesColors.ivory} />
            </TouchableOpacity>
          </View>

          <View
            style={[
              styles.headerBadge,
              {
                backgroundColor: 'rgba(8, 10, 24, 0.92)',
                borderColor: 'rgba(246, 190, 58, 0.42)',
              },
            ]}
          >
            <View
              style={[
                styles.headerIconWrap,
                {
                  backgroundColor: 'rgba(246, 190, 58, 0.18)',
                  shadowColor: iqesColors.gold,
                },
              ]}
            >
              <Ionicons name="people-outline" size={22} color={iqesColors.goldSoft} style={styles.headerIcon} />
            </View>
            <View style={styles.headerTextWrap}>
              <Text style={[styles.headerEyebrow, { color: iqesColors.goldSoft }]}>
                {groupLabel}
              </Text>
              <Text style={[styles.headerTitle, { color: iqesColors.ivory }]}>
                {heroTitle}
              </Text>
            </View>
          </View>
          <Text style={[styles.headerSubtitle, { color: iqesColors.mutedWarm }]}>
            {isPremiumHost
              ? t('multiplayer_lobby.lobbySubtitle', language)
              : canCreateRoom
              ? t('multiplayer_lobby.freeHostBadge', language)
              : t('multiplayer_lobby.memberRoomDescription', language).replace('{{groupType}}', groupLabelLower)}
          </Text>
        </View>

        <View
          style={[
            styles.sectionCard,
            {
              backgroundColor: 'rgba(8, 10, 24, 0.9)',
              borderColor: 'rgba(246, 190, 58, 0.38)',
            },
          ]}
        >
          <View style={styles.sectionHeaderCopy}>
            <Text style={[styles.sectionEyebrow, { color: iqesColors.goldSoft }]}>
              {sectionEyebrow}
            </Text>
            <Text style={[styles.sectionTitle, { color: iqesColors.ivory }]}>
              {sectionTitle}
            </Text>
            <Text style={[styles.sectionDescription, { color: iqesColors.mutedWarm }]}>
              {supportDescription}
            </Text>
          </View>
          {!sponsorAccessLoading && canCreateRoom ? (
            <TouchableOpacity
              onPress={handleCreateRoom}
              style={styles.secondaryCreateButtonWrap}
              activeOpacity={0.9}
            >
              <View
                style={[
                  styles.secondaryCreateButton,
                  {
                    backgroundColor: iqesColors.royalPurple,
                    shadowColor: iqesColors.gold,
                  },
                ]}
              >
                <Ionicons name="add-circle-outline" size={18} color="#FFFFFF" style={{ marginRight: 8 }} />
                <Text style={styles.secondaryCreateButtonText}>
                  {createButtonLabel}
                </Text>
              </View>
            </TouchableOpacity>
          ) : (
            <View style={[styles.memberHintChip, { backgroundColor: 'rgba(255, 248, 232, 0.08)' }]}>
              <Ionicons name="shield-checkmark-outline" size={16} color={iqesColors.goldSoft} style={{ marginRight: 8 }} />
              <Text style={[styles.memberHintText, { color: iqesColors.mutedWarm }]}>
                {t('multiplayer_lobby.memberRoomChip', language)}
              </Text>
            </View>
          )}
        </View>

        <ActiveRoomsList
          rooms={filteredRooms}
          loading={roomsLoading || familiesLoading}
          userId={profile.id}
          renderLobby={renderLobby}
          sectionPaddingBottom={72}
          groupTypeLabelLower={groupLabelLower}
          emptyTitle={emptyRoomsTitle}
          emptySubtitle={emptyRoomsSubtitle}
          data-oid="ay1r8b:"
        />

        {/* Player Preview Modal */}
        <PlayerPreviewModal
          visible={showPreviewModal}
          roomName={previewRoomName}
          players={players as unknown as GamePlayer[]}
          currentRoom={currentRoom as unknown as GameRoom | null}
          loading={previewLoading}
          onClose={() => setShowPreviewModal(false)}
          data-oid="q-q:1z4"
        />
        </ScrollView>

        {/* Chat Removed for MVP - Improved UX and Focus on Core Gameplay */}
        {/* This change reclaims ~50% of screen space on mobile devices */}
        {/* Future versions can add collapsible chat with better UX */}
      </View>
    </SafeAreaView>
  </LinearGradient>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  centeredShell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  centeredShellText: {
    marginTop: 14,
    color: iqesColors.goldSoft,
    fontSize: 16,
    fontWeight: '800',
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 12,
    paddingTop: 4,
  },
  headerBadge: {
    width: '90%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 28,
    marginBottom: 8,
    borderWidth: 1,
    shadowColor: '#F6BE3A',
    shadowOpacity: 0.16,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  headerIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
    elevation: 5,
  },
  headerIcon: {
    marginRight: 0,
  },
  headerTextWrap: {
    flex: 1,
  },
  headerEyebrow: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
  },
  headerSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    paddingHorizontal: 24,
    lineHeight: 20,
  },
  sectionCard: {
    width: '90%',
    alignSelf: 'center',
    borderRadius: 28,
    borderWidth: 1,
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 16,
    marginBottom: 14,
    shadowColor: '#F6BE3A',
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  sectionHeaderCopy: {
    marginBottom: 12,
  },
  sectionEyebrow: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 21,
    lineHeight: 26,
    fontWeight: '800',
    marginBottom: 4,
  },
  sectionDescription: {
    fontSize: 14,
    lineHeight: 19,
  },
  secondaryCreateButtonWrap: {
    width: '100%',
  },
  secondaryCreateButton: {
    borderRadius: 18,
    paddingVertical: 13,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
    elevation: 5,
  },
  secondaryCreateButtonText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 15,
  },
  orbTop: {
    position: 'absolute',
    top: -96,
    right: -96,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: 'rgba(139, 92, 246, 0.18)',
  },
  orbBottom: {
    position: 'absolute',
    bottom: -130,
    left: -130,
    width: 310,
    height: 310,
    borderRadius: 155,
    backgroundColor: 'rgba(246, 190, 58, 0.09)',
  },
  memberHintChip: {
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  memberHintText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
  },
});
